/**
 * ZenMarkup Privacy Analytics — Line Chart X-axis module.
 * Adaptive date labels with collision detection.
 */

const LABEL_MIN_GAP = 12;
const TARGET_LABELS = 6;
const NICE_STEPS = [1, 2, 5, 7, 14, 30];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

/**
 * Pick the smallest "nice" step that produces <= TARGET_LABELS labels.
 *
 * @param {number} len  Total data points.
 * @return {number}
 */
function pickNiceStep(len) {
	for (let i = 0; i < NICE_STEPS.length; i++) {
		if (Math.ceil(len / NICE_STEPS[i]) <= TARGET_LABELS) {
			return NICE_STEPS[i];
		}
	}
	return NICE_STEPS[NICE_STEPS.length - 1];
}

/**
 * Format date label based on range length.
 *
 * - Hourly data (len=24): "14:00"
 * - <=7 days: "Mon 14" (day name + date)
 * - <=90 days: "Mar 14" (month + date)
 * - >90 days: "Mar"
 *
 * @param {string} dateStr  ISO date or datetime string.
 * @param {number} len      Total number of data points.
 * @return {string}
 */
export function formatLabel(dateStr, len) {
	if (dateStr.length > 10) {
		return dateStr.substring(11, 16);
	}

	const p = dateStr.split('-');
	const month = parseInt(p[1], 10);
	const day = parseInt(p[2], 10);

	if (len > 90) {
		return MONTHS[month - 1];
	}

	if (len <= 7) {
		const d = new Date(parseInt(p[0], 10), month - 1, day);
		return DAYS[d.getDay()] + ' ' + day;
	}

	return MONTHS[month - 1] + ' ' + day;
}

/**
 * Draw X-axis date labels with collision detection.
 *
 * @param {Object} opts
 * @param {CanvasRenderingContext2D} opts.ctx
 * @param {Array}  opts.timeseries
 * @param {number} opts.w           Canvas logical width.
 * @param {number} opts.h           Canvas logical height.
 * @param {Object} opts.PAD         Padding object { top, right, bottom, left }.
 * @param {number} opts.hoverIdx    Currently hovered point index (-1 = none).
 * @param {string} opts.labelFont   Font string for labels.
 * @param {string} opts.labelColor  Fill color for labels.
 */
export function drawXAxis(opts) {
	const ctx = opts.ctx;
	const timeseries = opts.timeseries;
	const w = opts.w;
	const h = opts.h;
	const PAD = opts.PAD;
	const hoverIdx = opts.hoverIdx;
	const len = timeseries.length;
	if (!len) return;

	const chartW = w - PAD.left - PAD.right;
	const stepX = len > 1 ? chartW / (len - 1) : 0;
	const y = h - PAD.bottom + 16;

	ctx.font = opts.labelFont;
	ctx.fillStyle = opts.labelColor;
	ctx.textBaseline = 'top';

	const step = pickNiceStep(len);

	// Build candidate indices: step-aligned + always first + last.
	const last = len - 1;
	const indices = [0];
	for (let i = step; i < last; i += step) {
		indices.push(i);
	}
	if (last > 0) {
		indices.push(last);
	}

	// Collision detection: measure each label, drop if overlapping.
	const slots = indices.map(function (idx) {
		const text = formatLabel(timeseries[idx].date, len);
		const tw = ctx.measureText(text).width;
		const px = PAD.left + idx * stepX;
		return { idx: idx, text: text, x: px, w: tw };
	});

	// Keep first and last always. Filter middle labels by overlap.
	const kept = [slots[0]];
	for (let j = 1; j < slots.length - 1; j++) {
		const prev = kept[kept.length - 1];
		const cur = slots[j];
		const prevRight = prev.x + (prev.idx === 0 ? 0 : prev.w / 2);
		const curLeft = cur.x - cur.w / 2;
		if (curLeft - prevRight >= LABEL_MIN_GAP) {
			kept.push(cur);
		}
	}
	// Check last against final kept middle label.
	if (slots.length > 1) {
		const lastSlot = slots[slots.length - 1];
		const prevKept = kept[kept.length - 1];
		const prevR = prevKept.x + (prevKept.idx === 0 ? 0 : prevKept.w / 2);
		const lastL = lastSlot.x - lastSlot.w;
		if (lastL - prevR < LABEL_MIN_GAP && kept.length > 1) {
			kept.pop();
		}
		kept.push(lastSlot);
	}

	// Hover collision zone: skip static labels too close to hovered point.
	let hoverX = -9999;
	if (hoverIdx >= 0) {
		hoverX = PAD.left + hoverIdx * stepX;
	}

	// Render labels with edge-aware alignment.
	for (let k = 0; k < kept.length; k++) {
		const s = kept[k];
		if (hoverIdx >= 0 && Math.abs(s.x - hoverX) < s.w + LABEL_MIN_GAP) {
			continue;
		}
		if (s.idx === 0) {
			ctx.textAlign = 'left';
		} else if (s.idx === last) {
			ctx.textAlign = 'right';
		} else {
			ctx.textAlign = 'center';
		}
		ctx.fillText(s.text, s.x, y);
	}
}
